/**
 * LivingTileStudio — public "turn your own photo into a living tile" try-it.
 *
 * The headline Custom-board capability, made touchable on the marketing site
 * (founder direction 2026-07-12: "it's supposed to take a pic and do it for
 * the user… AI can do it… this is a Custom board — not manual, not instant").
 *
 * Honesty rules (no fake success):
 *   - The parent's uploaded photo is handled ENTIRELY on-device (FileReader →
 *     object URL). Nothing is uploaded, stored, or sent anywhere.
 *   - The gentle motion applied here is REAL, client-side CSS — it works
 *     offline on any photo, today.
 *   - The richer AI gesture-motion (a wave, arms opening) is the app's
 *     generation step; we describe it plainly and point to the LIVE sample
 *     tiles as proof, we do not pretend to generate it here.
 *   - Living tiles are a CUSTOM-board feature; manual and instant/starter
 *     boards are static. Stated on the page.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Sparkles, Volume2, ShieldCheck, Lock } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { speak, cancelSpeech, isSupported as ttsSupported } from "@/lib/tts";
import "@/components/demoboard/demoboard.css";

const MOTIONS = [
  { id: "breathe", label: "Breathe", hint: "a soft, calm zoom" },
  { id: "sway", label: "Sway", hint: "a gentle side-to-side" },
  { id: "float", label: "Float", hint: "a slow rise and settle" },
];

export default function LivingTileStudio() {
  useSEO({
    title: "Make a living tile from your photo",
    description:
      "Turn a photo of someone your child loves into a talking, moving tile. " +
      "Handled entirely on your device. A SonSpeak Custom-board feature.",
    path: "/living-studio",
  });

  const [consent, setConsent] = useState(false);
  const [photo, setPhoto] = useState(null); // object URL
  const [word, setWord] = useState("");
  const [motion, setMotion] = useState("breathe");
  const [live, setLive] = useState(true);
  const fileRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    cancelSpeech();
  }, []);

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    const url = URL.createObjectURL(file);
    urlRef.current = url;
    setPhoto(url);
    if (!word) setWord(file.name.replace(/\.[^.]+$/, "").slice(0, 24));
  };

  const sayWord = () => {
    if (!ttsSupported() || !word.trim()) return;
    cancelSpeech();
    speak(word.trim(), { rate: 0.95, pitch: 1.05 });
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="living-studio-page">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-6">
        <Link to="/demo" className="pill-btn mb-6 inline-flex" data-testid="ls-back">
          <ArrowLeft size={14} /> Back to the board
        </Link>
        <div className="flex items-center gap-2 mb-3">
          <span className="display text-[10px] uppercase tracking-[0.18em] bg-[color:var(--gold)] border-[2px] border-[color:var(--ink)] rounded-full px-2 py-0.5 font-extrabold">
            Custom board feature
          </span>
        </div>
        <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-4">
          Make a tile <span className="text-[color:var(--coral)]">come alive</span> — with your photo.
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 leading-relaxed max-w-2xl">
          Pick a photo of someone your child loves. It becomes a talking tile
          that gently moves — so the face lights up the moment they reach for
          it. Your photo stays <strong>on your device</strong>; nothing is
          uploaded.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-16 grid md:grid-cols-2 gap-6 items-start">
        {/* Left: the controls */}
        <div className="space-y-4">
          <div className="brutal-card p-4 bg-[color:var(--paper)]" data-testid="ls-consent-card">
            <label className="flex items-start gap-2 cursor-pointer text-sm leading-snug">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[color:var(--coral)]"
                data-testid="ls-consent"
              />
              <span>
                I&apos;m the parent/guardian, everyone in the photo has agreed to
                be used, and I have the right to use it.
              </span>
            </label>
          </div>

          <div className="brutal-card p-4 bg-white">
            <div className="display font-extrabold text-sm mb-2">1 · Choose a photo</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPick}
              disabled={!consent}
              className="hidden"
              data-testid="ls-file"
            />
            <button
              type="button"
              className="pill-btn primary w-full justify-center"
              disabled={!consent}
              onClick={() => fileRef.current && fileRef.current.click()}
              data-testid="ls-choose"
            >
              <Camera size={16} /> {photo ? "Choose a different photo" : "Choose a photo"}
            </button>
            {!consent && (
              <p className="text-xs text-[color:var(--ink-soft)] mt-2">
                Check the box above first.
              </p>
            )}
          </div>

          <div className="brutal-card p-4 bg-white">
            <div className="display font-extrabold text-sm mb-2">2 · The word</div>
            <input
              className="brutal-input w-full"
              value={word}
              onChange={(e) => setWord(e.target.value.slice(0, 24))}
              placeholder="e.g. Mom"
              maxLength={24}
              data-testid="ls-word"
            />
            <p className="text-xs text-[color:var(--ink-soft)] mt-2">
              In the app you record this in <strong>your own voice</strong>. Here
              it uses your device&apos;s voice.
            </p>
          </div>

          <div className="brutal-card p-4 bg-white">
            <div className="display font-extrabold text-sm mb-2">3 · Gentle motion</div>
            <div className="flex flex-wrap gap-2">
              {MOTIONS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`setup-chip ${motion === m.id ? "is-selected" : ""}`}
                  aria-pressed={motion === m.id}
                  onClick={() => setMotion(m.id)}
                  data-testid={`ls-motion-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[color:var(--ink-soft)] mt-2">
              {MOTIONS.find((m) => m.id === motion)?.hint} — real, offline motion
              on any photo.
            </p>
          </div>
        </div>

        {/* Right: the live preview tile */}
        <div className="md:sticky md:top-6">
          <div className="brutal-card p-5 bg-[color:var(--cream-deep)]">
            <div className="display font-extrabold text-sm mb-3 text-center">Your living tile</div>
            <div className="ls-preview-wrap">
              <div className="demoboard-tile is-living ls-preview-tile" style={{ "--cat": "var(--brand-blue, #4D96FF)" }}>
                <div className="tile-topstrip" style={{ background: "var(--brand-blue, #4D96FF)" }} aria-hidden />
                <div className="tile-art">
                  {photo ? (
                    <span
                      className={`tile-pic ${live ? `ls-motion-${motion}` : ""}`}
                      style={{ backgroundImage: `url(${photo})` }}
                      aria-hidden
                    />
                  ) : (
                    <span className="tile-photoframe" aria-hidden>
                      <Camera className="tile-photocam" />
                      <span className="tile-photohint">your photo</span>
                    </span>
                  )}
                  {photo && <span className="live-badge" aria-hidden>LIVE</span>}
                </div>
                <div className="tile-label">{word.trim() || "your word"}</div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                type="button"
                className="pill-btn primary"
                onClick={sayWord}
                disabled={!word.trim()}
                data-testid="ls-speak"
              >
                <Volume2 size={16} /> Say it
              </button>
              <button
                type="button"
                className={`pill-btn ${live ? "" : "opacity-70"}`}
                onClick={() => setLive((v) => !v)}
                aria-pressed={live}
                data-testid="ls-live-toggle"
              >
                {live ? "Motion on" : "Motion off"}
              </button>
            </div>
          </div>

          {/* The AI upgrade — honest framing, no fake generation */}
          <div className="brutal-card p-4 bg-white mt-4" data-testid="ls-ai-note">
            <div className="display font-extrabold text-sm mb-1 flex items-center gap-2">
              <Sparkles size={16} aria-hidden /> Bring it fully to life with AI
            </div>
            <p className="text-xs text-[color:var(--ink)]/75 leading-snug">
              In the SonSpeak app, the Custom board can go further: from a photo
              (or a 1–2 second clip), the AI makes a real motion — a wave, arms
              opening for a hug — that plays once when your child taps, then
              settles. You approve it before it ever reaches their board. See the{" "}
              <Link to="/demo" className="underline font-semibold">LIVE tiles on the demo</Link>{" "}
              for what that looks like.
            </p>
          </div>

          <div className="brutal-card p-4 mt-4" style={{ background: "#FFF0EE", borderColor: "var(--coral-deep)" }}>
            <div className="text-xs leading-snug flex items-start gap-2" style={{ color: "var(--ink)" }}>
              <Lock size={14} className="shrink-0 mt-0.5" aria-hidden />
              <span>
                Living tiles are a <strong>Custom board</strong> feature. The
                manual and instant starter boards use clear, still pictures.
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[color:var(--ink-soft)]">
            <ShieldCheck size={13} aria-hidden />
            Your photo never leaves this device.
          </div>
        </div>
      </section>
    </div>
  );
}
